import type { Data } from "@puckeditor/core";
import type { PuckDiffOp } from "@ease/page-builder";

type BuilderBlock = {
  type: string;
  props: Record<string, unknown>;
  _id?: string;
};

type BlockLocation = {
  container: "content" | "zone";
  zoneId?: string;
  index: number;
};

type LocatedBlock = {
  id: string;
  block: BuilderBlock;
  location: BlockLocation;
};

const ZONE_PREFIX = "zone:";

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isBuilderBlock(value: unknown): value is BuilderBlock {
  return isRecord(value) && typeof value.type === "string" && isRecord(value.props);
}

function getBlockId(block: BuilderBlock): string | null {
  const componentId = block.props.id;
  return typeof componentId === "string" && componentId.length > 0 ? componentId : null;
}

function normalizeContent(content: unknown): BuilderBlock[] {
  return Array.isArray(content) ? content.filter(isBuilderBlock) : [];
}

function normalizeZones(zones: unknown): Record<string, BuilderBlock[]> {
  if (!isRecord(zones)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(zones).map(([zoneId, value]) => [zoneId, normalizeContent(value)]),
  );
}

function listContainers(data: Data): Array<{ key: string; blocks: BuilderBlock[]; location: Omit<BlockLocation, "index"> }> {
  const zones = normalizeZones(data.zones);
  return [
    { key: "content", blocks: normalizeContent(data.content), location: { container: "content" } },
    ...Object.entries(zones).map(([zoneId, blocks]) => ({
      key: `${ZONE_PREFIX}${zoneId}`,
      blocks,
      location: { container: "zone" as const, zoneId },
    })),
  ];
}

function flattenBlocks(data: Data): LocatedBlock[] {
  return listContainers(data).flatMap(({ blocks, location }) =>
    blocks.flatMap((block, index) => {
      const id = getBlockId(block);
      return id
        ? [{ id, block, location: { ...location, index } }]
        : [];
    }),
  );
}

function buildLocationMap(data: Data): Map<string, LocatedBlock> {
  return new Map(flattenBlocks(data).map((entry) => [entry.id, entry]));
}

function containerBlocks(data: Data, location: Omit<BlockLocation, "index">): BuilderBlock[] {
  if (location.container === "content") {
    return normalizeContent(data.content);
  }

  return normalizeZones(data.zones)[location.zoneId ?? ""] ?? [];
}

function buildPositionToken(data: Data, location: BlockLocation): string {
  const items = containerBlocks(data, location);
  const before = items[location.index - 1];
  const beforeId = before ? getBlockId(before) : null;
  const prefix = location.container === "zone" ? `${ZONE_PREFIX}${location.zoneId}:` : "";

  if (beforeId) {
    return `${prefix}after:${beforeId}`;
  }

  return `${prefix}start`;
}

function parsePositionToken(position: string): { location: Omit<BlockLocation, "index">; anchor: "start" | "end" | "after" | "before"; anchorId?: string } {
  let remaining = position;
  let location: Omit<BlockLocation, "index"> = { container: "content" };

  if (remaining.startsWith(ZONE_PREFIX)) {
    const separator = remaining.indexOf(":after:");
    const beforeSeparator = remaining.indexOf(":before:");
    const startSeparator = remaining.indexOf(":start");
    const endSeparator = remaining.indexOf(":end");
    const candidates = [separator, beforeSeparator, startSeparator, endSeparator].filter((value) => value > -1);
    const splitAt = candidates.length > 0 ? Math.min(...candidates) : remaining.length;
    const zoneId = remaining.slice(ZONE_PREFIX.length, splitAt);
    location = { container: "zone", zoneId };
    remaining = remaining.slice(splitAt + 1);
  }

  if (remaining === "start") {
    return { location, anchor: "start" };
  }

  if (remaining === "end") {
    return { location, anchor: "end" };
  }

  if (remaining.startsWith("after:")) {
    return { location, anchor: "after", anchorId: remaining.slice("after:".length) };
  }

  if (remaining.startsWith("before:")) {
    return { location, anchor: "before", anchorId: remaining.slice("before:".length) };
  }

  return { location, anchor: "end" };
}

function diffProps(before: Record<string, unknown>, after: Record<string, unknown>): Record<string, unknown> {
  const changed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(after)) {
    if (JSON.stringify(before[key]) !== JSON.stringify(value)) {
      changed[key] = value;
    }
  }

  return changed;
}

function findSingleAddedId(before: Data, after: Data): string | null {
  const beforeIds = new Set(flattenBlocks(before).map((entry) => entry.id));
  const added = flattenBlocks(after).filter((entry) => !beforeIds.has(entry.id));
  return added.length === 1 ? added[0]!.id : null;
}

function findSingleRemovedId(before: Data, after: Data): string | null {
  const afterIds = new Set(flattenBlocks(after).map((entry) => entry.id));
  const removed = flattenBlocks(before).filter((entry) => !afterIds.has(entry.id));
  return removed.length === 1 ? removed[0]!.id : null;
}

export function createCollabDiff(before: Data, after: Data): PuckDiffOp | null {
  const beforeMap = buildLocationMap(before);
  const afterMap = buildLocationMap(after);

  if (afterMap.size === beforeMap.size + 1) {
    const addedId = findSingleAddedId(before, after);
    if (!addedId) {
      return null;
    }
    const added = afterMap.get(addedId);
    if (!added) {
      return null;
    }
    return {
      action: "add_component",
      position: buildPositionToken(after, added.location),
      component: structuredClone(added.block),
    };
  }

  if (beforeMap.size === afterMap.size + 1) {
    const removedId = findSingleRemovedId(before, after);
    if (!removedId) {
      return null;
    }
    return {
      action: "remove_component",
      componentId: removedId,
    };
  }

  for (const [id, beforeEntry] of beforeMap) {
    const afterEntry = afterMap.get(id);
    if (!afterEntry) {
      continue;
    }

    if (
      beforeEntry.location.container !== afterEntry.location.container
      || beforeEntry.location.zoneId !== afterEntry.location.zoneId
      || beforeEntry.location.index !== afterEntry.location.index
    ) {
      return {
        action: "move_component",
        componentId: id,
        newPosition: buildPositionToken(after, afterEntry.location),
      };
    }

    const changedProps = diffProps(beforeEntry.block.props, afterEntry.block.props);
    if (Object.keys(changedProps).length > 0) {
      return {
        action: "update_props",
        componentId: id,
        props: changedProps,
      };
    }
  }

  return null;
}

function replaceContainer(data: Data, location: Omit<BlockLocation, "index">, nextBlocks: BuilderBlock[]): Data {
  if (location.container === "content") {
    return {
      ...data,
      content: nextBlocks as Data["content"],
    };
  }

  return {
    ...data,
    zones: {
      ...normalizeZones(data.zones),
      [location.zoneId ?? ""]: nextBlocks,
    } as Data["zones"],
  };
}

function resolveInsertIndex(blocks: BuilderBlock[], anchor: "start" | "end" | "after" | "before", anchorId?: string): number {
  if (anchor === "start") {
    return 0;
  }

  if (anchor === "end") {
    return blocks.length;
  }

  const anchorIndex = blocks.findIndex((block) => getBlockId(block) === anchorId);
  if (anchorIndex < 0) {
    return blocks.length;
  }

  return anchor === "after" ? anchorIndex + 1 : anchorIndex;
}

function removeComponentFromData(data: Data, componentId: string): { next: Data; removed: BuilderBlock | null } {
  const locationMap = buildLocationMap(data);
  const entry = locationMap.get(componentId);
  if (!entry) {
    return { next: data, removed: null };
  }

  const blocks = [...containerBlocks(data, entry.location)];
  const [removed] = blocks.splice(entry.location.index, 1);
  return {
    next: replaceContainer(data, entry.location, blocks),
    removed: removed ?? null,
  };
}

export function applyCollabDiffToData(data: Data, diff: PuckDiffOp): Data {
  switch (diff.action) {
    case "update_props": {
      const locationMap = buildLocationMap(data);
      const entry = locationMap.get(diff.componentId);
      if (!entry) {
        return data;
      }
      const blocks = [...containerBlocks(data, entry.location)];
      blocks[entry.location.index] = {
        ...blocks[entry.location.index]!,
        props: {
          ...blocks[entry.location.index]!.props,
          ...diff.props,
        },
      };
      return replaceContainer(data, entry.location, blocks);
    }
    case "remove_component": {
      return removeComponentFromData(data, diff.componentId).next;
    }
    case "add_component": {
      const { location, anchor, anchorId } = parsePositionToken(diff.position);
      const blocks = [...containerBlocks(data, location)];
      const insertIndex = resolveInsertIndex(blocks, anchor, anchorId);
      blocks.splice(insertIndex, 0, structuredClone(diff.component) as BuilderBlock);
      return replaceContainer(data, location, blocks);
    }
    case "move_component": {
      const removed = removeComponentFromData(data, diff.componentId);
      if (!removed.removed) {
        return data;
      }
      const { location, anchor, anchorId } = parsePositionToken(diff.newPosition);
      const blocks = [...containerBlocks(removed.next, location)];
      const insertIndex = resolveInsertIndex(blocks, anchor, anchorId);
      blocks.splice(insertIndex, 0, removed.removed);
      return replaceContainer(removed.next, location, blocks);
    }
    default:
      return data;
  }
}

export function describeCollabDiff(diff: PuckDiffOp): string {
  switch (diff.action) {
    case "add_component":
      return `Added ${diff.component.type}`;
    case "remove_component":
      return `Removed ${diff.componentId}`;
    case "move_component":
      return `Moved ${diff.componentId}`;
    case "update_props":
      return `Updated ${diff.componentId}`;
    default:
      return "Updated page";
  }
}
